import { useMemo } from 'react';
import { resourceCountEnvironments } from '@gonfalon/dogfood-flags/dogfood-flags';
import { groupStatesToStateChanges } from '@gonfalon/observability-graphql/alert-state-adapter';
import {
  useGetAgentArtifactQuery,
  useGetAlertQuery,
  useGetLastAlertGroupStatesQuery,
} from '@gonfalon/observability-graphql/hooks';
import type { AlertState } from '@gonfalon/observability-graphql/schemas';
import { getAutomatedReleasesQuery } from '@gonfalon/rest-api/queries/automatedReleases';
import { getEnvironmentsByProjectQuery } from '@gonfalon/rest-api/queries/environments';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useQuery } from '@tanstack/react-query';

import type { FlagEnvSummary } from '../../../../internal/components/AgentInsights/components/artifacts/deriveFlagEnvSummaries';
import {
  deriveFlagEnvSummaries,
  deriveFlagOverallLabel,
} from '../../../../internal/components/AgentInsights/components/artifacts/deriveFlagEnvSummaries';
import {
  countLatestPullRequestReviews,
  derivePullRequestStateLabel,
  isAlertArtifactType,
  isFlagArtifactType,
  isPullRequestArtifactType,
  parseAlertArtifactIdentity,
  parseFlagArtifactIdentity,
} from '../../../../internal/components/AgentInsights/components/artifacts/parseArtifactIdentity';

const CARD_STALE_TIME_MS = 30_000;
const MAX_ENVIRONMENTS_QUERY_LIMIT = 1000;

export interface ArtifactCardStoredMeta {
  type: string;
  externalId: string;
  title?: string | null;
  url?: string | null;
  status?: string | null;
  updatedAt?: string | number | null;
}

export interface ArtifactCardPrData {
  kind: 'pr';
  loading: boolean;
  unavailable: boolean;
  stateLabel?: string;
  checks?: { total: number; success: number; failure: number; pending: number };
  approvals?: number;
  changesRequested?: number;
  repoLabel?: string;
  liveUpdatedAt?: string | number | null;
  liveTitle?: string | null;
  liveUrl?: string | null;
}

export interface ArtifactCardFlagData {
  kind: 'flag';
  loading: boolean;
  unavailable: boolean;
  error?: boolean;
  environments: FlagEnvSummary[];
  overallLabel?: string;
}

export interface ArtifactCardAlertData {
  kind: 'alert';
  loading: boolean;
  unavailable: boolean;
  enabled?: boolean;
  state?: AlertState | string;
  lastEvaluatedAt?: string | number | null;
  liveName?: string | null;
}

export interface ArtifactCardGenericData {
  kind: 'generic';
  loading: boolean;
  unavailable: boolean;
}

export type ArtifactCardLiveData =
  | ArtifactCardPrData
  | ArtifactCardFlagData
  | ArtifactCardAlertData
  | ArtifactCardGenericData;

interface Args {
  projectId: string;
  projectKey: string;
  artifact: ArtifactCardStoredMeta;
}

function usePullRequestCardData(projectId: string, artifact: ArtifactCardStoredMeta): ArtifactCardPrData {
  const skip = !projectId || !artifact.type || !artifact.externalId;
  const { data, loading, error } = useGetAgentArtifactQuery({
    variables: {
      project_id: projectId,
      type: artifact.type,
      external_id: artifact.externalId,
    },
    skip,
    fetchPolicy: 'cache-first',
  });

  const pr = data?.agentArtifact?.github_pull_request;
  if (loading && !pr) {
    return { kind: 'pr', loading: true, unavailable: false };
  }
  if (error || !pr) {
    return { kind: 'pr', loading: false, unavailable: true };
  }

  const { approvals, changesRequested } = countLatestPullRequestReviews(pr.reviews);

  return {
    kind: 'pr',
    loading: false,
    unavailable: false,
    stateLabel: derivePullRequestStateLabel(pr),
    checks: {
      total: pr.checks.total,
      success: pr.checks.success,
      failure: pr.checks.failure,
      pending: pr.checks.pending,
    },
    approvals,
    changesRequested,
    repoLabel: `${pr.owner}/${pr.repo}#${pr.number}`,
    liveUpdatedAt: pr.updated_at,
    liveTitle: pr.title,
    liveUrl: pr.html_url,
  };
}

export function useFlagArtifactData(
  projectKey: string,
  artifact: ArtifactCardStoredMeta,
  maxEnvironments = 3,
): ArtifactCardFlagData {
  const identity = parseFlagArtifactIdentity(artifact.externalId);
  const enabled = Boolean(identity);

  const flagQuery = useQuery({
    ...getFeatureFlagQuery({
      projectKey: identity?.projectKey ?? projectKey,
      featureFlagKey: identity?.flagKey ?? '',
    }),
    enabled,
    staleTime: CARD_STALE_TIME_MS,
  });

  const envsQuery = useQuery({
    ...getEnvironmentsByProjectQuery({
      projectKey: identity?.projectKey ?? projectKey,
      query: {
        sort: 'critical',
        // Default API page size is 20; status must consider every critical env.
        limit: Math.min(resourceCountEnvironments(), MAX_ENVIRONMENTS_QUERY_LIMIT),
      },
    }),
    enabled,
    staleTime: CARD_STALE_TIME_MS,
  });

  const automatedQuery = useQuery({
    ...getAutomatedReleasesQuery({
      projectKey: identity?.projectKey ?? projectKey,
      flagKey: identity?.flagKey ?? '',
    }),
    enabled,
    staleTime: CARD_STALE_TIME_MS,
  });

  const loading =
    enabled &&
    ((flagQuery.isPending && !flagQuery.data) ||
      (envsQuery.isPending && !envsQuery.data) ||
      (automatedQuery.isPending && !automatedQuery.data));

  const allEnvironments = useMemo(() => {
    if (!enabled || !flagQuery.data || !envsQuery.data) {
      return [];
    }
    const items = envsQuery.data.items ?? [];
    return deriveFlagEnvSummaries({
      flagEnvironments: flagQuery.data.environments,
      projectEnvironments: items.map((e) => ({
        key: e.key,
        name: e.name,
        critical: Boolean(e.critical),
        id: e._id,
      })),
      releasePhases: automatedQuery.data?.items ?? [],
      max: Number.MAX_SAFE_INTEGER,
    });
  }, [automatedQuery.data, enabled, envsQuery.data, flagQuery.data]);

  const environments = allEnvironments.slice(0, Math.max(0, maxEnvironments));

  if (!enabled) {
    return { kind: 'flag', loading: false, unavailable: true, error: false, environments: [] };
  }
  if (loading) {
    return { kind: 'flag', loading: true, unavailable: false, error: false, environments: [] };
  }
  // Automated releases only enrich rollout copy. A release-API failure must not
  // hide basic on/off status from the flag and environment queries.
  const hasBlockingError = (flagQuery.isError && !flagQuery.data) || (envsQuery.isError && !envsQuery.data);
  if (hasBlockingError) {
    return { kind: 'flag', loading: false, unavailable: true, error: true, environments: [] };
  }
  if (!flagQuery.data || !envsQuery.data) {
    return { kind: 'flag', loading: false, unavailable: true, error: false, environments: [] };
  }

  return {
    kind: 'flag',
    loading: false,
    unavailable: false,
    error: false,
    environments,
    overallLabel: deriveFlagOverallLabel(allEnvironments),
  };
}

function useAlertCardData(artifact: ArtifactCardStoredMeta): ArtifactCardAlertData {
  const identity = parseAlertArtifactIdentity(artifact.externalId);
  const skip = !identity;

  const { data, loading, error } = useGetAlertQuery({
    variables: { id: identity?.alertId ?? '' },
    skip,
    fetchPolicy: 'cache-first',
  });

  const {
    data: groupStateData,
    loading: stateLoading,
    error: stateError,
  } = useGetLastAlertGroupStatesQuery({
    variables: { alert_id: identity?.alertId ?? '' },
    skip,
    fetchPolicy: 'cache-first',
  });

  const hasStateData = groupStateData !== undefined;

  if (skip) {
    return { kind: 'alert', loading: false, unavailable: true };
  }

  const alert = data?.alert;
  // Wait for both the alert record and evaluation state before leaving the
  // loading skeleton, so we don't flash a benign "Enabled" badge.
  if ((loading && !alert) || (stateLoading && !hasStateData && !stateError)) {
    return { kind: 'alert', loading: true, unavailable: false };
  }
  if (error || !alert) {
    return { kind: 'alert', loading: false, unavailable: true };
  }

  const changes = groupStatesToStateChanges(groupStateData?.last_alert_group_states);
  const latest = changes[0] ?? null;
  // State-change errors degrade only the evaluation line — enabled/name still useful.
  const stateUnavailable = Boolean(stateError) && !latest;

  return {
    kind: 'alert',
    loading: false,
    unavailable: false,
    enabled: !alert.disabled,
    state: stateUnavailable ? undefined : latest?.state,
    lastEvaluatedAt: latest?.timestamp ?? alert.updated_at,
    liveName: alert.name,
  };
}

/**
 * Per-card live data adapter. Only the matching type's queries run; other kinds
 * short-circuit to a generic/unavailable result. Failures degrade independently.
 */
export function useArtifactCardData({ projectId, projectKey, artifact }: Args): ArtifactCardLiveData {
  const isPr = isPullRequestArtifactType(artifact.type);
  const isFlag = isFlagArtifactType(artifact.type);
  const isAlert = isAlertArtifactType(artifact.type);

  // Hooks must run unconditionally; skip/enabled gates prevent network work.
  const prData = usePullRequestCardData(isPr ? projectId : '', artifact);
  const flagData = useFlagArtifactData(isFlag ? projectKey : '', isFlag ? artifact : { ...artifact, externalId: '' });
  const alertData = useAlertCardData(isAlert ? artifact : { ...artifact, externalId: '' });

  if (isPr) {
    return prData;
  }
  if (isFlag) {
    return flagData;
  }
  if (isAlert) {
    return alertData;
  }
  return { kind: 'generic', loading: false, unavailable: false };
}
